import Button from '@/components/atoms/button';
import Input from '@/components/atoms/input';
import { tryGetMemo } from '@/core/presenter/controller/HomeController';
import * as ViewModel from '@/core/presenter/viewModel';
import { useStores } from '@/stores';
import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';

interface IProps {
  isWritting: boolean;
  sendAddress: string;
  onChangeSendAddress: (e: React.SyntheticEvent<HTMLInputElement>) => void;
  onClickDeleteBtn: () => void;
  onPaste: (text: string) => void;
  onClickCameraBtn: () => void;
  onSetMemo?: (memo: string) => void;
  onClipboardError?: () => void;
}

const SendInputForm = ({
  isWritting,
  sendAddress,
  onChangeSendAddress,
  onClickDeleteBtn,
  onPaste,
  onClickCameraBtn,
  onSetMemo,
  onClipboardError,
}: IProps) => {
  const { userStore } = useStores();
  const prevAddressRef = useRef<string>(sendAddress);

  /** 주소가 변경될 때 메모 초기화 및 자동 조회 */
  useEffect(() => {
    const prevAddress = prevAddressRef.current;
    const isAddressChanged = prevAddress !== sendAddress;

    if (isAddressChanged) {
      if (prevAddress !== '') {
        // 주소가 변경되었을 때 메모 초기화 (초기 렌더링 제외)
        if (onSetMemo) {
          onSetMemo('');
        }
      }

      // QR 코드나 붙여넣기로 주소가 한 번에 30자 이상 설정된 경우 자동으로 메모 조회
      // (이전 주소가 30자 미만이었는데 갑자기 30자 이상이 되면)
      if (prevAddress.length < 30 && sendAddress.length >= 30 && userStore.user?.id) {
        // tryGetMemo 호출 전에 먼저 메모 초기화
        if (onSetMemo) {
          onSetMemo('');
        }
        tryGetMemo(userStore.user.id, sendAddress).then((res) => {
          if (onSetMemo) {
            // 성공했을 때 result 값 설정 (빈 문자열이면 빈 문자열로 설정)
            if (res.succed) {
              onSetMemo(res.result ?? '');
            }
          }
        });
      }
    }

    prevAddressRef.current = sendAddress;
  }, [sendAddress, userStore.user?.id, onSetMemo]);

  /** onBlur 이벤트에서 sendAddress 길이가 30자 이상일 때 tryGetMemo 실행 */
  const handleBlur = useCallback(() => {
    if (sendAddress.length >= 30 && userStore.user?.id) {
      // tryGetMemo 호출 전에 먼저 메모 초기화
      if (onSetMemo) {
        onSetMemo('');
      }
      tryGetMemo(userStore.user.id, sendAddress).then((res) => {
        if (onSetMemo) {
          // 성공했을 때 result 값 설정 (빈 문자열이면 빈 문자열로 설정)
          if (res.succed) {
            onSetMemo(res.result ?? '');
          }
        }
      });
    }
  }, [sendAddress, userStore.user?.id, onSetMemo]);

  /** 붙여넣기 핸들링 이벤트 */
  const handlePaste = useCallback(async () => {
    try {
      // 기본 Clipboard API 시도
      if (navigator.clipboard?.readText) {
        const text = await navigator.clipboard.readText();
        onPaste(text);
        return;
      }

      // Permissions API를 통한 권한 요청
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'clipboard-read' as PermissionName });
        if (result.state === 'granted' || result.state === 'prompt') {
          const text = await navigator.clipboard.readText();
          onPaste(text);
          return;
        }
      }

      if (onClipboardError) onClipboardError();
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      if (onClipboardError) onClipboardError();
    }
  }, [onPaste, onClipboardError]);

  return (
    <>
      <div className="send-address-input-group">
        <Input
          type="text"
          value={sendAddress}
          className="send-address-input text-base font-normal"
          id="addressInput"
          placeholder="공개 주소(T) 입력"
          validatorType={ViewModel.InputValidatorType.ALPHABET_NUMBER}
          onChange={onChangeSendAddress}
          onBlur={handleBlur}
        />
        {/* <i
          id="clearIcon"
          className="bi bi-x-circle"
          style={{ display: isWritting ? 'inline-block' : 'none' }}
          onClick={onClickDeleteBtn}
        ></i> */}
        <button style={{ display: isWritting ? 'inline-block' : 'none' }} type="button" className="clear-address-btn" onClick={onClickDeleteBtn}>
          <img src="./images/close-icon.png" alt="닫기" className="close-icon" />
        </button>
        {sendAddress.length === 0 && (
          <button type="button" className="qr-scan-btn" onClick={onClickCameraBtn}>
            <img src="./images/input-qr-code.png" alt="QR 코드 스캔" className="qr-scan-icon" />
          </button>
        )}
      </div>


      {sendAddress.length === 0 && (
        <div className="send-address-input-button-group right">
          <Button
            caption="붙여넣기"
            className={clsx('paste-btn text-sm font-medium', { none_important: isWritting, show_important: !isWritting })}
            onClick={handlePaste}
          />
        </div>
      )}
    </>
  );
};

export default SendInputForm;
